import React from 'react';
import {
  Button,
  TextButton,
  Text,
  Checkbox,
  Modal,
  Icon,
  Heading,
} from '@amzn/storm-ui';
import {
  ModalFooterActions,
  ConfirmationList,
  ConfirmBody,
  RequestSubmittedBody,
  ModalLoadingBody,
} from '../style';
import { AccountItem } from '../types';

interface ConfirmationModalProps {
  isOpen: boolean;
  hasPendingAccess: boolean;
  selectedItems: AccountItem[];
  confirmChecked: boolean;
  onConfirmCheckedChange: (checked: boolean) => void;
  onClose: () => void;
  onConfirm: () => void;
  // Copy for the terminal "Request submitted" state (requester journey).
  // Required rather than defaulted so this branch can never render hardcoded
  // English; see R14.4.
  pendingHeader: string;
  pendingBodyText: string;
  pendingEmailNote: string;
  doneLabel: string;
  // Copy for the confirm state. Required rather than optional-with-an-English-
  // default: a defaulted literal renders untranslated the moment a caller
  // omits it, and nothing fails to signal the gap. That is exactly how the
  // combine lane came to show nine English strings in every locale
  // (SGA-QA-Bug-590). Both lanes now pass Puff strings explicitly.
  header: string;
  bodyText: string;
  bullets: string[];
  // A node rather than a string because the upgrade lane embeds a link to the
  // Amazon Ads Agreement inside the sentence (SGA-QA-Bug-587). Storm's Checkbox
  // already types its label as ReactNode, so this only widens our own contract.
  checkboxLabel: React.ReactNode;
  submitLabel: string;
  cancelLabel: string;
  /**
   * Present while the confirm request is in flight, which replaces the body
   * with a spinner and blocks every way out of the dialog.
   *
   * The flag and its copy travel together as one object so a caller cannot
   * enable the state without supplying translated copy for it, and a caller
   * that never enters the state supplies nothing. That is the same reasoning as
   * the required copy props above, expressed for an optional branch.
   */
  submitting?: { label: string };
  /**
   * Width for the confirm body, when the caller's design specifies one.
   *
   * Storm's Modal is `width: fit-content` with only its own `max-width: 80%` as a
   * bound and no width prop, so an unconstrained body lets a long sentence run out
   * to that bound and the dialog reads far wider than designed. Each lane's design
   * gives a different width, so this is the caller's to state rather than something
   * this component can pick.
   */
  confirmBodyWidth?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  hasPendingAccess,
  selectedItems,
  confirmChecked,
  onConfirmCheckedChange,
  onClose,
  onConfirm,
  pendingHeader,
  pendingBodyText,
  pendingEmailNote,
  doneLabel,
  header,
  bodyText,
  bullets,
  checkboxLabel,
  submitLabel,
  cancelLabel,
  submitting,
  confirmBodyWidth,
}) => (
  <Modal
    isOpen={isOpen}
    header={hasPendingAccess ? pendingHeader : header}
    /*
      Dismissal is blocked while the request is in flight. The approved frame
      shows Cancel and the close control enabled, but the upgrade has already
      started server-side and cannot be undone, so dismissing here would leave
      the user with no way to learn whether it succeeded. That is exactly the gap
      the over-10-second progress snackbar fills, so the frame's enabled Cancel
      presumes that snackbar exists.

      Both halves are needed. Disabling the close button makes the state visible
      and matches the greyed Cancel, rather than leaving a control that looks
      clickable and silently does nothing. Neutralising onClose additionally
      covers the routes that do not go through that button, such as Escape.

      TODO: restore dismissal once the app-level snackbar lands (Flow 6), so
      closing hands the operation off rather than abandoning it.
    */
    closeButtonProps={{ disabled: !!submitting }}
    onClose={submitting ? () => undefined : onClose}
    footer={
      hasPendingAccess ? (
        <Button variant="primary" onClick={onClose}>
          {doneLabel}
        </Button>
      ) : (
        <ModalFooterActions data-testid="confirmation-modal-footer">
          <TextButton disabled={!!submitting} onClick={onClose}>
            {cancelLabel}
          </TextButton>
          <Button
            variant="primary"
            /*
              Storm's Button renders a spinner for `loading` but the test double
              does not derive `disabled` from it, so the disabled state is passed
              explicitly. Here the spinner lives in the body instead, per the
              frame, and this control is only ever inert.
            */
            disabled={!confirmChecked || !!submitting}
            onClick={onConfirm}
          >
            {submitLabel}
          </Button>
        </ModalFooterActions>
      )
    }
  >
    {submitting ? (
      /*
        role=status rather than a heading-only body: assistive tech should
        announce this as a transient status, politely, not interrupt with it.

        Composed here rather than reusing the shared Spinner component, whose
        wrapper stacks the icon above a default-weight Text. The frame puts the
        label beside the icon at heading size, so overriding that from outside
        would mean fighting its flex-direction and font with descendant
        selectors.
      */
      <ModalLoadingBody data-testid="confirmation-modal-loading" role="status">
        <Icon type="spinner" size="lg" aria-hidden="true" />
        <Heading renderAs="h2" size="medium">
          {submitting.label}
        </Heading>
      </ModalLoadingBody>
    ) : hasPendingAccess ? (
      // withMargin on each paragraph: Storm's Text renders a <p> but zeroes its
      // block margins by default, so without it these three lines stack flush.
      // The approved design (frame 1773:133542) separates them.
      <RequestSubmittedBody data-testid="request-submitted-modal-body">
        <Text withMargin>{pendingBodyText}</Text>
        <Text withMargin>
          {selectedItems
            .filter((a) => a.isPendingAccess)
            .map((a) => a.id)
            .join(', ')}
        </Text>
        <Text withMargin>{pendingEmailNote}</Text>
      </RequestSubmittedBody>
    ) : (
      <ConfirmBody $width={confirmBodyWidth}>
        <Text>{bodyText}</Text>
        <ConfirmationList>
          {bullets.map((bullet) => (
            /*
              Through Text rather than as bare list-item content, so a bullet is
              typeset exactly like the paragraph above it. A raw <li> inherits the
              dialog's font instead, which is larger than body text, and the design
              gives the body and the bullets one shared set of tokens. Matching by
              construction rather than by copying a size means the two cannot drift.
            */
            <li key={bullet}>
              <Text>{bullet}</Text>
            </li>
          ))}
        </ConfirmationList>
        <Checkbox
          id="confirm-checkbox"
          label={checkboxLabel}
          checked={confirmChecked}
          onChange={(e) => onConfirmCheckedChange(e.target.checked)}
        />
      </ConfirmBody>
    )}
  </Modal>
);

export default ConfirmationModal;
